import { clsx } from 'clsx';
import { type KeyboardEvent, useRef } from 'react';
import s from './ui.module.css';

export interface SegmentedProps<T extends string> {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  dark?: boolean;
  /** Layout overrides from the caller's CSS module (width, grid columns, item height). */
  className?: string;
}

/** Single-choice radio group with arrow-key navigation and roving tabindex. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  dark,
  className,
}: SegmentedProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (e: KeyboardEvent, index: number) => {
    const delta =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? -1
          : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next = (index + delta + options.length) % options.length;
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    refs.current[next]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={clsx(s.segmented, dark && s.segmentedDark, className)}
    >
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          className={s.segment}
          onClick={() => {
            onChange(o.value);
          }}
          onKeyDown={(e) => {
            move(e, i);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
